import { getReportBySystemTypeApi } from '@intelligence/services/reportService';
import { useInfiniteScroll } from 'ahooks';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getLocale, history, useIntl } from 'umi';

import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { config } from 'webpack';
import { DATABRAIN_COS_ACCELERATE, DATABRAIN_COS_DOMAIN } from '@intelligence/_config/config';
import { splitReportTagStr } from '@intelligence/v3/utils/topcharts';
import { getReportLanguages, normalizeLanguage, LANG_TAB_I18N_MAP } from '@intelligence/utils/reportLanguage';
import useGetState from '@intelligence/hooks/useGetState';
import { fetchReportConfig } from '@intelligence/hooks/useReportConfig';

const REPORT_SOURCE_MAPPING: IRecord<string> = {
  databrain: 'Newzoo',
};

const convertReportSource = (source?: string): string => {
  if (!source) return '';
  return REPORT_SOURCE_MAPPING[source.toLowerCase()] || source;
};

export default (props: { systemId: any }, tabList: any[]) => {
  const { formatMessage } = useIntl();
  const getDefaultActiveId = () => {
    const tab = history?.location?.query?.tab || '';
    if (tab) {
      const targetTab = tabList?.find((item) => item.name === tab);
      childrenData = targetTab?.children || [];
      return targetTab?.id || 'Newzoo';
    }
    return 'Newzoo';
  };
  let childrenData: any = [];
  const [dataFull, setDataFull]: any = useState({});
  const [activeId, setActiveId, getActiveId] = useGetState(getDefaultActiveId());
  const [searchValue, setSearchValue] = useState('');
  const [dataPartial, setDataPartial] = useState([]);
  const [dataShow, setDataShow]: any[] = useState([]);
  const [dataLoading, setLoading] = useState(true);
  const [segmentValue, setSegmentValue] = useState('All');
  const [languageOption, setLanguageOption] = useState({});
  const [option, setOption] = useState([]);
  const [reportSourceConfig, setReportSourceConfig] = useState({});
  const debounce = useRef({ interval: 0, delay: 500 }).current;
  const all = ['全部', 'All'];
  const { systemId } = props;

  const lazyScrollRef = useRef<HTMLDivElement>(null);

  const pageLogMes = dbiPageLogMes.MarketReports;

  interface Result {
    list: [];
    nextId: number | undefined;
  }

  const locale = getLocale();
  /**
   * @description: 获得全部的Report列表
   * @return {*}
   */
  const getReportList = async () => {
    setLoading(true);
    try {
      const res = await getReportBySystemTypeApi(systemId, { order_bys: ['update_time desc'] });
      // console.log(props.systemId, res.resource_list);
      const tmp: any = {
        Newzoo: [],
        'Sensor Tower': [],
        Ampere: [],
        VGI: [],
        'data.ai': [],
        'Stream Hatchet': [],
        NPD: [],
        Statista: [],
        Famitsu: [],
        Twitter: [],
        Fandom: [],
        Others: [],
        AppMagic: [],
      };
      const languageOption = {
        Newzoo: { All: all },
        'Sensor Tower': { All: all },
        Ampere: { All: all },
        VGI: { All: all },
        'data.ai': { All: all },
        'Stream Hatchet': { All: all },
        NPD: { All: all },
        Statista: { All: all },
        Famitsu: { All: all },
        Twitter: { All: all },
        Fandom: { All: all },
        Others: { All: all },
        AppMagic: { All: all },
      };
      const itmKeyList = Object.keys(tmp)?.map((item) => item.toLowerCase()) || [];
      // console.log(itmKeyList)
      if (Array.isArray(res?.resource_list) && res?.resource_list.length > 0) {
        res.resource_list.forEach((item, index) => {
          const itemSource = convertReportSource(item?.source);
          if (tmp.hasOwnProperty(itemSource) || itmKeyList.includes(itemSource.toLowerCase())) {
            const targetKey =
              Object.keys(tmp)?.find((config) => config.toLowerCase() === itemSource.toLowerCase()) || '';
            // const time = moment(item.update_time).format('YYYY-MM-DD');
            const time = moment(item.create_time).format('YYYY-MM-DD');

            const coverImg = item.cover?.replace(DATABRAIN_COS_DOMAIN, DATABRAIN_COS_ACCELERATE);
            const itemTag = splitReportTagStr(item?.tag);
            const itemTagEN = splitReportTagStr(item?.tag_en);

            const hasNewLangFields = item?.file_cn_language || item?.file_en_language;
            const { fileCnLang, fileEnLang } = getReportLanguages(item, 'reportLanguage');
            const obj = {
              ...item,
              key: index,
              resource_id: item.resource_id,
              // coverImg: item.cover,
              coverImg,
              // 筛选时高亮会修改 title, 给选中文案添加标签样式
              // 避免 title 变化有影响使用，保留原有 title 信息到 originTitle
              title: [item.title, item.title_en],
              originTitle: [item.title, item.title_en],
              report_type: [item.report_type, item.report_type_en],
              report_lang: hasNewLangFields
                ? [fileCnLang, fileEnLang]
                : [item?.report_language || fileCnLang, item?.report_language_en || fileEnLang],
              summary: [item.introduction, item.introduction_en],
              tag: [itemTag, itemTagEN],
              tag_en: itemTagEN,
              time,
              source: itemSource,
            };
            tmp[targetKey]?.push(obj);
            if (fileCnLang) {
              const normalized = normalizeLanguage(fileCnLang);
              if (!(normalized in languageOption[targetKey])) {
                languageOption[targetKey][normalized] = hasNewLangFields
                  ? [normalized, normalized]
                  : [item?.report_language || normalized, item?.report_language_en || normalized];
              }
            }
            if (fileEnLang) {
              const normalized = normalizeLanguage(fileEnLang);
              if (!(normalized in languageOption[targetKey])) {
                languageOption[targetKey][normalized] = hasNewLangFields
                  ? [normalized, normalized]
                  : [item?.report_language || normalized, item?.report_language_en || normalized];
              }
            }
          }
        });
      }
      // 获取最新的 activeId
      const currentActiveId = getActiveId();
      // console.log(languageOption)
      setLanguageOption(languageOption);
      getOptionNew(languageOption, currentActiveId);
      setDataFull(tmp);
      // 根据当前最新的 activeId 设置当前可搜索的Report列表，避免切换tab后旧请求覆盖数据
      setDataPartial(tmp[currentActiveId]);
      if (currentActiveId === 'Others') {
        setDataShow(
          [tmp[currentActiveId], tmp['data.ai'], tmp['Stream Hatchet'], tmp.Statista, tmp.Famitsu, tmp.Twitter].flat(99),
        );
      } else {
        setDataShow(tmp[currentActiveId]);
      }
      // setDataShow(tmp[activeId]);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const getOptionNew = (languageOption, value: string, newLang?: number) => {
    if (!languageOption[value]) return;
    const finalOption: any[] = [];
    const langTmp = lang ?? newLang;
    Object.entries(languageOption[value]).forEach(([k, v]) => {
      let text: string;
      if (k === 'All') {
        text = v?.[langTmp] || k;
      } else {
        const i18nKey = LANG_TAB_I18N_MAP[k];
        text = i18nKey ? formatMessage({ id: i18nKey }) : `${k} Report`;
      }
      finalOption.push({ value: k, text });
    });
    finalOption.forEach((item: any, index: number) => {
      if (item?.value === 'All') {
        finalOption.unshift(finalOption.splice(index, 1)[0]);
      }
    });
    setOption(finalOption);
  };

  /**
   * @description: 设置debounce，避免搜索频繁更改Report列表
   * @param {string} word 搜索关键字
   * @return {*}
   */
  const debounceSearchChange = (word: string) => {
    clearTimeout(debounce.interval);
    // @ts-ignore
    debounce.interval = setTimeout(() => {
      searchFilterData(word);
    }, debounce.delay);
  };

  const searchFilterData = (word: string) => {
    let newData = [];
    if (typeof word === 'string' && word === '') {
      newData = dataPartial;
    } else {
      dataPartial.forEach((item, idx) => {
        let title = '';
        // 根据locale设置搜索的标题

        title = item.title[lang];
        // console.log(title)
        // let itemUpper = title.toUpperCase();
        // let replaceWord = title.substr(itemUpper.indexOf(word.toUpperCase()), word.length);
        // let replaceHtml = `<span style="color:#326ff0">${replaceWord}</span>`;
        // const regExp = new RegExp(replaceWord, 'gi');
        // let newTitle = title.replace(regExp, replaceHtml);
        const regex = new RegExp(`(${word.replace(/ /g, '\\s*')})`, 'gi');

        if (regex.test(title)) {
          // 替换匹配项为带有highlight类的span标签
          const newTitle = title.replace(regex, '<span style="color:#326ff0">$&</span>');
          let titleList = [];
          if (lang == 0) {
            titleList = [newTitle, item.title[1]];
          } else {
            titleList = [item.title[0], newTitle];
          }
          const obj = { ...item, title: titleList };
          newData.push(obj);
        }
      });
    }
    setDataShow(newData);
  };

  function getLoadMoreList(nextId: number | undefined, limit: number): Promise<Result> {
    let start = 0;
    if (nextId) {
      start = dataShow.findIndex((i) => i.key === nextId);
    }
    const end = start + limit;
    const list = dataShow.slice(start, end);
    const nId = dataShow[end] ? dataShow[end].key : undefined;

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          list,
          nextId: nId,
        });
      }, 500);
    });
  }

  const { data, loading, loadingMore, noMore } = useInfiniteScroll(
    (d) => {
      // console.log(d)
      return getLoadMoreList(d?.nextId, 6);
    },
    {
      target: lazyScrollRef,
      isNoMore: (d) => d?.nextId === undefined,
      reloadDeps: [dataShow],
    },
  );

  /** 获取报告展示配置，失败时保留默认空配置 */
  const getReportConfig = async () => {
    try {
      const reportConfig = await fetchReportConfig();
      setReportSourceConfig(reportConfig);
    } catch (error) {
      console.error('[MarketReports] fetch report config failed:', error);
      setReportSourceConfig({});
    }
  };

  /**
   * @description: 初始化一次性获得所有的Report列表
   * @return {*}
   */
  useEffect(() => {
    setSegmentValue(all[1]);
    getReportList();
    getReportConfig();
    const targetTab = tabList?.find((item) => item.id === activeId);
    if (targetTab) {
      if (dbiPageLogMes?.[targetTab?.pagePoint]) {
        buriedPointPage(dbiPageLogMes?.[targetTab?.pagePoint]);
      }
    } else {
      buriedPointPage(pageLogMes);
    }
  }, []);

  /**
   * @description: 获得当前的语言
   * @return {*}
   */
  const lang = useMemo(() => {
    let lang = 0;
    if (locale == 'zh-CN') {
      lang = 0;
    } else {
      lang = 1;
    }
    getOptionNew(languageOption, activeId, lang);
    return lang;
  }, [locale]);

  /**
   * @description: 切换source来源tab时抹除搜索框和设置Report列表
   * @return {*}
   */
  useEffect(() => {
    setSearchValue('');
    if (activeId === 'Others') {
      setDataShow(
        [
          dataFull[activeId],
          dataFull['data.ai'],
          dataFull['Stream Hatchet'],
          dataFull.Statista,
          dataFull.Famitsu,
          dataFull.Twitter,
        ].flat(99),
      );
    } else {
      setDataShow(dataFull[activeId]);
    }
    setDataPartial(dataFull[activeId]);
    getOptionNew(languageOption, activeId);
    setSegmentValue(all[1]);
  }, [activeId]);

  useEffect(() => {
    setSearchValue('');
    let newData = [];
    if (segmentValue === 'All') {
      newData = dataPartial?.slice(0) || [];
    } else {
      const normalizedSegment = normalizeLanguage(segmentValue);
      dataPartial.forEach((item) => {
        const { fileCnLang, fileEnLang } = getReportLanguages(item, 'reportLanguage');
        if (
          normalizeLanguage(fileCnLang) === normalizedSegment ||
          normalizeLanguage(fileEnLang) === normalizedSegment
        ) {
          newData.push(item);
        }
      });
    }
    setDataShow(newData);
  }, [segmentValue]);

  // console.log(dataPartial)

  return {
    reportSourceConfig,
    dataShow,
    setActiveId,
    activeId,
    setSearchValue,
    searchValue,
    lang,
    debounceSearchChange,
    dataLoading,
    segmentValue,
    setSegmentValue,
    option,
    systemId,
    lazyScrollRef,
    data,
    loading,
    loadingMore,
    noMore,
    childrenData,
  };
};
